import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3x3, Minus, Plus } from 'lucide-react';
import { IconButton, Menu } from '../../ui';
import { Icon } from '../../ui/icons';
import type { EditorController } from './controller';
import { FLOATING_BAR } from './canvas-ui';

const LEVELS = [0.5, 1, 2] as const;

/** Zoom out, level (menu: 50/100/200 %, fit, selection) and zoom in, bottom right (spec §H.4); every gesture has a button (§14). */
export function ZoomControls({ controller, onToggleSnap }: { controller: EditorController; onToggleSnap?: () => void }) {
  const { t } = useTranslation();
  const snap = useSyncExternalStore(controller.subscribeUi, controller.snapEnabled);
  const z = useSyncExternalStore(controller.subscribeCamera, () => controller.camera.z);
  const pct = `${Math.round(z * 100)} %`;
  const run = (fn: () => void) => () => {
    fn();
    controller.focus();
  };
  return (
    <div data-canvas-ignore="" className={`absolute bottom-4 right-4 z-10 flex items-center gap-0.5 rounded-full p-1 font-sans text-fg ${FLOATING_BAR} max-sm:hidden`}>
      <IconButton label={t('commands.zoomOut')} icon={<Icon icon={Minus} size={16} />} size="sm" onClick={run(() => controller.runCommand('zoomOut'))} />
      <Menu.Root>
        <Menu.Trigger
          aria-label={`${t('zoom.level')} ${pct}`}
          className="h-7 min-w-14 rounded-full px-1.5 font-mono pointer-coarse:h-11 text-xs text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus data-[popup-open]:bg-surface-2"
        >
          {pct}
        </Menu.Trigger>
        <Menu.Popup label={t('zoom.level')} side="top" align="end" sideOffset={10}>
          {LEVELS.map((level) => (
            <Menu.Item key={level} onClick={run(() => controller.zoomTo(level))}>
              {`${level * 100} %`}
            </Menu.Item>
          ))}
          <Menu.Separator />
          <Menu.Item onClick={run(() => controller.runCommand('zoomFit'))}>{t('commands.zoomFit')}</Menu.Item>
          <Menu.Item onClick={run(() => controller.runCommand('zoomSelection'))}>{t('commands.zoomSelection')}</Menu.Item>
        </Menu.Popup>
      </Menu.Root>
      <IconButton label={t('commands.zoomIn')} icon={<Icon icon={Plus} size={16} />} size="sm" onClick={run(() => controller.runCommand('zoomIn'))} />
      <IconButton
        label={t('mainMenu.snapToGrid')}
        icon={<Icon icon={Grid3x3} size={16} />}
        size="sm"
        pressed={snap}
        onClick={run(onToggleSnap ?? (() => controller.setSnapEnabled(!controller.snapEnabled())))}
      />
    </div>
  );
}
